import React from 'react'
import { FeatureGroup, TileLayer, Pane } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import GlobalStyle, { StyledMap } from './styles'
import Curve from './Curve'
import Bounds, { DirectBounds } from './Bounds'
import MapEvents from './MapEvents'
import Marker from './Marker'
import {
  ShapeStyles,
  MapServiceType,
  RouteData,
  RouteFlight,
  RoutePath,
  DirectRoute,
  CurvePathOptions,
} from './mapTypes'
import {
  addConnectorFlight,
  changePathLocation,
  uniqifyPaths,
  addFlightId,
  uniqifyFlights,
  isTouchDevice,
} from './mapUtils'

export interface Props {
  canZoom: boolean
  shapeStyles: ShapeStyles
  selectedRouteId: string | null
  routes?: RouteData[]
  directRoutes?: DirectRoute[]
  defaultCenter?: [number, number]
  defaultZoom?: number
  className?: string
  hoveredRouteId?: string | null
  service?: MapServiceType
  placeholderComponent?: React.ReactNode
  handleMouseOverRoute?: (id: string) => void
  handleClickRoute?: (id: string | null) => void
  handleMouseOutRoute?: () => void
  handleClickDirectRouteMarker?: (route: DirectRoute) => void
  padding?: [number, number]
  paddingMobile?: [number, number]
}

const DEFAULT_MAP_CENTER: [number, number] = [40, 40],
  DEFAULT_MAP_ZOOM = 3,
  SOUTH_WEST = L.latLng(-90, -500),
  NORTH_EAST = L.latLng(90, 500),
  DEFAULT_SEVICE = {
    url: `https://api.mapbox.com/styles/v1/disco18/cktidce7u402n17mo9ljyxtu9/tiles/256/{z}/{x}/{y}@2x?access_token=pk.eyJ1IjoiZGlzY28xOCIsImEiOiJja3RqMG00Mjkwd25qMnlvNmhleGZ0YXp6In0.ecMBE1sNEfNx4-yLmM1_8g`,
    attribution:
      '© <a href="https://apps.mapbox.com/feedback/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
  DEFAULT_MIN_MAX_ZOOM = {
    min: 2,
    max: 7,
  }

const Map = (props: Props) => {
  const {
    canZoom,
    shapeStyles,
    selectedRouteId,
    routes,
    directRoutes,
    defaultCenter = DEFAULT_MAP_CENTER,
    defaultZoom = DEFAULT_MAP_ZOOM,
    className,
    hoveredRouteId,
    service = DEFAULT_SEVICE,
    placeholderComponent,
    handleMouseOverRoute,
    handleClickRoute,
    handleMouseOutRoute,
    handleClickDirectRouteMarker,
    padding,
    paddingMobile
  } = props
  const [hoveredId, setHoveredId] = React.useState<string | null>(null)
  const canTouch = isTouchDevice()

  const renderCurve = React.useCallback(
    (flight: RouteFlight, path: RoutePath[], options: CurvePathOptions, i: number) => {
      const fromPosition = path.find(({ name }) => name === flight.from)
      const toPosition = path.find(({ name }) => name === flight.to)
      if (fromPosition && toPosition)
        return (
          <>
            <Curve
              key={i}
              startPoint={[fromPosition.lat, fromPosition.lon]}
              endPoint={[toPosition.lat, toPosition.lon]}
              options={options}
            />
            <Curve
              key={i}
              startPoint={[fromPosition.lat, fromPosition.lon]}
              endPoint={[toPosition.lat, toPosition.lon]}
              options={{
                ...options,
                opacity: 0,
                weight: 20
              }}
            />
          </>
        )
      return null
    },
    []
  )

  const newRoutes = React.useMemo(
    () => (routes?.length ? addFlightId(addConnectorFlight(changePathLocation(routes))) : null),
    [routes]
  )

  const routesToShow = React.useMemo(
    () => (newRoutes ? newRoutes.filter((route) => route.isVisible) : null),
    [newRoutes]
  )

  const markersToShow = React.useMemo(
    () => (routesToShow ? uniqifyPaths(routesToShow.map(({ path }) => path)) : null),
    [routesToShow]
  )

  const curvesToShow = React.useMemo(
    () => (routesToShow ? uniqifyFlights(routesToShow.map(({ flights }) => flights)) : null),
    [routesToShow]
  )

  const getRestMarkers = React.useCallback(
    (routes: RouteData[]) => {
      const invisibleRoutes = routes.filter((route) => !route.isVisible).map(({ path }) => path)

      if (invisibleRoutes.length < 1 || !markersToShow) return null

      const flattened = invisibleRoutes[0].concat(...invisibleRoutes.slice(1))

      const pathMarkers = flattened.filter(
        (pathObj) =>
          !markersToShow.some((showPath) =>
            showPath.some(({ lat, lon }) => lat === pathObj.lat && lon === pathObj.lon)
          )
      )

      return pathMarkers
    },
    [markersToShow]
  )

  const markersRest = React.useMemo(() => (newRoutes ? getRestMarkers(newRoutes) : null), [
    newRoutes,
    getRestMarkers,
  ])

  const handleClickMap = React.useCallback(() => handleClickRoute?.(null), [handleClickRoute])

  const handleMouseOut = React.useCallback(() => handleMouseOutRoute?.() ?? setHoveredId(null), [
    handleMouseOutRoute,
  ])

  const handleMouseOver = React.useCallback(
    (id: string) => handleMouseOverRoute?.(id) ?? setHoveredId(id),
    [handleMouseOverRoute]
  )

  return (
    <React.Fragment>
      <GlobalStyle />
      <StyledMap
        center={defaultCenter}
        zoom={defaultZoom}
        attributionControl={false}
        zoomControl={false}
        placeholder={placeholderComponent}
        scrollWheelZoom={canZoom}
        doubleClickZoom={canZoom}
        minZoom={DEFAULT_MIN_MAX_ZOOM.min}
        maxZoom={DEFAULT_MIN_MAX_ZOOM.max}
        maxBounds={L.latLngBounds(SOUTH_WEST, NORTH_EAST)}
        className={className}
        zoomDelta={0.8}
        tapTolerance={100}
        tap
      >
        <TileLayer {...service} />
        {!canTouch && <MapEvents handleClick={handleClickMap} handleOver={handleMouseOut} />}
        {directRoutes && <DirectBounds routes={directRoutes} />}
        {newRoutes && <Bounds routes={newRoutes} padding={padding} paddingMobile={paddingMobile} />}
        {!routesToShow &&
          directRoutes &&
          directRoutes.map((route, i) => {
            const isStartPoint = i === 0
            const color = isStartPoint ? shapeStyles.selectedColor : shapeStyles.directMarkerColor
            const content = isStartPoint
              ? route.iata
              : `Direct flights from ${directRoutes![0].iata} to ${route.iata}`
            const onClick = isStartPoint ? undefined : () => handleClickDirectRouteMarker?.(route)

            return (
              <FeatureGroup key={i} eventHandlers={{ click: onClick }}>
                <Marker
                  content={content}
                  styles={{ color }}
                  radius={shapeStyles.defaultMarkerRadius}
                  center={[route.lat, route.lon]}
                />
              </FeatureGroup>
            )
          })}
        {markersRest &&
          markersRest.map((path, i) => {
            const color = shapeStyles.disabledColor
            return (
              <Marker
                key={i}
                content={path.name}
                styles={{ color }}
                radius={shapeStyles.disabledMarkerRadius}
                center={[path.lat, path.lon]}
              />
            )
          })}

        {routesToShow &&
          routesToShow.map((route, i) => {
            const selectedRoute = routesToShow.find(({ id }) => id === selectedRouteId)
            const hoveredRoute = routesToShow.find(({ id }) => id === (hoveredRouteId ?? hoveredId))
            const selectedFlights = selectedRoute?.flights
            const hoveredFlights = hoveredRoute?.flights
            const selectedPath = selectedRoute?.path
            const hoveredPath = hoveredRoute?.path
            const onClick = (e: L.LeafletEvent) => {
              L.DomEvent.stopPropagation(e)
              handleClickRoute?.(route.id)
            }

            const handlers = canTouch ? {
              click: onClick
            } : {
              click: onClick,
              mouseover: () => handleMouseOver(route.id),
              mouseout: handleMouseOut
            }

            return (
              <FeatureGroup
                key={route.id}
                eventHandlers={handlers}
              >
                {markersToShow &&
                  markersToShow[i] &&
                  markersToShow[i].map((marker, j) => {
                    const isSelected = selectedPath?.some(
                      ({ lon, lat }) => lon === marker.lon && lat === marker.lat
                    )
                    const isHovered = hoveredPath?.some(
                      ({ lon, lat }) => lon === marker.lon && lat === marker.lat
                    )
                    const color = isSelected
                      ? shapeStyles.selectedColor
                      : isHovered
                      ? shapeStyles.hoveredColor
                      : shapeStyles.defaultColor

                    return isSelected || isHovered ? (
                      <Pane key={j} name={route.id + marker.lat + marker.lon}>
                        <Marker
                          content={marker.name}
                          styles={{ color }}
                          radius={shapeStyles.defaultMarkerRadius}
                          center={[marker.lat, marker.lon]}
                        />
                      </Pane>
                    ) : (
                      <Marker
                        key={j}
                        content={marker.name}
                        styles={{ color }}
                        radius={shapeStyles.defaultMarkerRadius}
                        center={[marker.lat, marker.lon]}
                      />
                    )
                  })}
                {curvesToShow &&
                  curvesToShow[i] &&
                  curvesToShow[i].map((curve, j) => {
                    const isSelected = selectedFlights?.some(({ id }) => id === curve.id)
                    const isHovered = hoveredFlights?.some(({ id }) => id === curve.id)
                    const color = isSelected
                      ? shapeStyles.selectedColor
                      : isHovered
                      ? shapeStyles.hoveredColor
                      : shapeStyles.defaultColor
                    const pane = isSelected || isHovered ? 'overlayPane' : 'tilePane'

                    return renderCurve(
                      curve,
                      route.path,
                      {
                        color,
                        weight: shapeStyles.curveWeight,
                        pane,
                      },
                      j
                    )
                  })}
              </FeatureGroup>
            )
          })}
      </StyledMap>
    </React.Fragment>
  )
}

export default React.memo(Map)
